import { APIProvider, Map as GoogleMap } from "@vis.gl/react-google-maps";
import { EntityMarkers } from "./entity/EntityMarkers";
import { MapLayers } from "./entity/MapLayers";
import { useState, useMemo } from "react";
import { MapContext } from "./MapContext";
import { MapInfoWindow } from "./elements/MapInfoWindow";
import { CurrentLocationPin } from "./kiosk/CurrentLocationPin";
import { EntityInteraction } from "./entity/EntityInteraction";
import { calculateMapBounds } from "./calculateMapBounds";
import { getIsQueryStringTruthy } from "@lib/url";
import { PlanRoute } from "./userPlan/PlanRoute";
import { EntityRoute } from "./entity/EntityRoute";
import { DirectionsRefresher } from "../../modules/plan/PlanNavigate/DirectionsRefresher";
import { PlanEditRoute } from "./userPlan/PlanEditRoute";
import { MapBounds } from "./MapBounds";
import { useWindowSize } from "@hooks/useWindowSize/useWindowSize";
import { EntityChildrenMarkers } from "./entity/EntityChildrenMarkers";
import { MobileExitButton } from "./elements/MobileExitButton/MobileExitButton";
import { UserLocationPin } from "./elements/UserLocationPin";
import { StreetView } from "./StreetView";
import { MapControls } from "./elements/MapControls/MapControls";
import { getClientCoordinates } from "@lib/getClientCoordinates";

import "./GoogleMapOverrides.css";

const zoom = VisitWidget.settings.currentClientMapZoomLevel;
const gestureHandling = getIsQueryStringTruthy(
  "disable_map_cooperative_gesture_handling",
  true,
)
  ? "greedy"
  : "auto";

const Map = () => {
  const [selectedMarker, setSelectedMarker] = useState<any | null>(null);
  const { isMobile } = useWindowSize();
  const defaultBounds = useMemo(() => {
    return calculateMapBounds(getClientCoordinates(), zoom, isMobile);
  }, [isMobile]);

  return (
    <MapContext.Provider value={{ selectedMarker, setSelectedMarker }}>
      <APIProvider apiKey={VisitWidget.settings.mapBrowserApiKey}>
        <GoogleMap
          gestureHandling={gestureHandling}
          id="primary-map"
          mapId={
            undefined
            /*
             * Map ID *cannot* be used because it will only use styles from the cloud
             * and ignore styles prop below. Migrating to cloud styles is in part
             * difficult because no json editor exists anymore. Additionally, the use
             * of Advanced Markers requires map id be defined and thus we can't use
             * Advanced Markers until we solve the map style migration.
             */
          }
          defaultBounds={{
            ...defaultBounds.region,
            padding: defaultBounds.padding,
          }}
          scaleControl={false}
          zoomControl={false}
          cameraControl={false}
          mapTypeControl={false}
          fullscreenControl={false}
          streetViewControl={!isMobile}
          controlSize={32}
          styles={[
            ...(VisitWidget.mapTemplate ?? []),
            {
              featureType: "poi",
              elementType: "all",
              stylers: [{ visibility: "off" }],
            },
          ]}>
          <EntityMarkers />
          <MapLayers />
          <MapInfoWindow />
          <CurrentLocationPin />
          <EntityInteraction />
          <DirectionsRefresher />
          <PlanRoute />
          <PlanEditRoute />
          <EntityChildrenMarkers />
          <EntityRoute />
          <MapBounds />
          <MobileExitButton />
          <StreetView />
          <UserLocationPin />
          <MapControls />
        </GoogleMap>
      </APIProvider>
    </MapContext.Provider>
  );
};

export { Map };
